/**
 * app-state-api.ts
 *
 * API helpers for /api/users/me/app-state (GET/PUT/DELETE) and the
 * platform_tour.v1 typed codec for namespace consumer.overview.
 *
 * Routes route through `api` from src/lib/api.ts — never raw fetch.
 */
import { api } from "./api";
import {
  isTourStatus,
  type TourState,
  type TourStatus,
} from "../components/overview/platform-tour";

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

/** Nested shape returned by GET /api/users/me/app-state */
export type AppStateResponse = {
  app_state: Record<string, Record<string, unknown>>;
};

/** PUT /api/users/me/app-state/:namespace/:key response */
export type AppStatePutResponse = {
  namespace: string;
  key: string;
  value: unknown;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const NAMESPACE = "consumer.overview";
const KEY = "platform_tour.v1";

const DASHBOARD_NAMESPACE = "consumer.dashboard";
const FINISH_PROFILE_KEY = "finish_profile.v1";

const FUNNEL_NAMESPACE = "consumer.funnel";
const BANK_MFA_KEY = "bank_mfa.v1";

/**
 * GET /api/users/me/app-state
 * Returns the full nested {namespace:{key:value}} map.
 */
export async function fetchAppState(token: string): Promise<AppStateResponse> {
  return api.get<AppStateResponse>("/api/users/me/app-state", token);
}

/**
 * PUT /api/users/me/app-state/consumer.overview/platform_tour.v1
 */
export async function putTourState(
  state: TourState,
  token: string,
): Promise<AppStatePutResponse> {
  return api.put<AppStatePutResponse>(
    `/api/users/me/app-state/${NAMESPACE}/${KEY}`,
    { value: encodeTourState(state) },
    token,
  );
}

/**
 * DELETE /api/users/me/app-state/consumer.overview/platform_tour.v1
 * Returns 204 (undefined). Idempotent — deleting a missing row is fine.
 */
export async function deleteTourState(token: string): Promise<void> {
  return api.delete<void>(`/api/users/me/app-state/${NAMESPACE}/${KEY}`, token);
}

/**
 * PUT /api/users/me/app-state/consumer.dashboard/finish_profile.v1
 *
 * Durably records that the user has acknowledged the finish-profile nudge so
 * it never re-shows — across reloads and devices — replacing the prior
 * sessionStorage-only "shown once" semantics.
 */
export async function putFinishProfileAck(
  status: FinishProfileAckStatus,
  token: string,
): Promise<AppStatePutResponse> {
  return api.put<AppStatePutResponse>(
    `/api/users/me/app-state/${DASHBOARD_NAMESPACE}/${FINISH_PROFILE_KEY}`,
    { value: { status } },
    token,
  );
}

export function hasPendingBankMFA(
  appState: Record<string, Record<string, unknown>>,
): boolean {
  const raw = appState[FUNNEL_NAMESPACE]?.[BANK_MFA_KEY];
  return (
    !!raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    (raw as Record<string, unknown>).status === "pending"
  );
}

// ---------------------------------------------------------------------------
// platform_tour.v1 codec
// ---------------------------------------------------------------------------

/** Wire shape of the value stored in user_app_state for platform_tour.v1 */
export type PlatformTourStateValue = {
  status: TourStatus;
  step: number;
};

/**
 * encodeTourState converts a TourState to the backend value object.
 * The backend stores the value as-is (JSON object).
 */
export function encodeTourState(state: TourState): PlatformTourStateValue {
  return { status: state.status, step: state.step };
}

/**
 * decodeTourState parses the raw backend value into a TourState.
 *
 * Rejects unknown statuses — returns null instead of silently treating
 * them as terminal. Unknown status is NOT normalized to any terminal state.
 * Unknown step is clamped to 0 (safe).
 *
 * Returns null for malformed input so callers treat it as missing state.
 */
export function decodeTourState(raw: unknown): TourState | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const obj = raw as Record<string, unknown>;
  const status = obj["status"];
  const stepRaw = obj["step"];

  // Reject unknown statuses — never treat unknown as terminal.
  if (!isTourStatus(status)) {
    return null;
  }

  const step =
    typeof stepRaw === "number" && Number.isFinite(stepRaw)
      ? Math.max(0, Math.floor(stepRaw))
      : 0;

  return { status, step };
}

/**
 * extractTourStateFromBootstrap pulls the platform_tour.v1 state out of the
 * nested bootstrap app_state object and decodes it.
 *
 * Returns null when app_state is absent, the namespace/key is missing, or
 * the value fails codec validation. Callers treat null as missing backend state.
 */
export function extractTourStateFromBootstrap(
  appState: Record<string, Record<string, unknown>> | null | undefined,
): TourState | null {
  if (!appState) {
    return null;
  }

  const ns = appState[NAMESPACE];
  if (!ns || typeof ns !== "object") {
    return null;
  }

  const raw = ns[KEY];
  if (raw === undefined) {
    return null;
  }

  return decodeTourState(raw);
}

// ---------------------------------------------------------------------------
// finish_profile.v1 codec
// ---------------------------------------------------------------------------

/**
 * Terminal acknowledgement of the finish-profile nudge. Both values mean
 * "the user has seen and acted on the nudge — never auto-show it again":
 * `completed` (clicked through to the passport) vs `dismissed` ("Maybe later").
 */
export type FinishProfileAckStatus = "dismissed" | "completed";

/** Narrow an unknown value to a known acknowledgement status. */
export function isFinishProfileAckStatus(
  value: unknown,
): value is FinishProfileAckStatus {
  return value === "dismissed" || value === "completed";
}

/**
 * decodeFinishProfileAck parses the raw backend value into an ack status.
 *
 * Returns null for malformed input or an unknown status so callers treat it
 * as "not yet acknowledged" (the nudge may still show) — never silently
 * coerced into a terminal state.
 */
export function decodeFinishProfileAck(
  raw: unknown,
): FinishProfileAckStatus | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const status = (raw as Record<string, unknown>)["status"];
  return isFinishProfileAckStatus(status) ? status : null;
}

/**
 * extractFinishProfileAckFromBootstrap pulls the finish_profile.v1 ack out of
 * the nested bootstrap app_state object and decodes it.
 *
 * Returns null when app_state is absent, the namespace/key is missing, or the
 * value fails codec validation — callers treat null as "not acknowledged".
 */
export function extractFinishProfileAckFromBootstrap(
  appState: Record<string, Record<string, unknown>> | null | undefined,
): FinishProfileAckStatus | null {
  if (!appState) {
    return null;
  }

  const ns = appState[DASHBOARD_NAMESPACE];
  if (!ns || typeof ns !== "object") {
    return null;
  }

  const raw = ns[FINISH_PROFILE_KEY];
  if (raw === undefined) {
    return null;
  }

  return decodeFinishProfileAck(raw);
}

// ---------------------------------------------------------------------------
// referral_banner.v1 — dashboard referral banner dismissal
// ---------------------------------------------------------------------------

const REFERRAL_BANNER_KEY = "referral_banner.v1";
const REFERRAL_PUBLISH_PROMPT_KEY = "referral_publish_prompt.v1";

/**
 * A recorded dismissal of the dashboard referral banner, plus the identity of
 * the incentive that was live when the user dismissed it (`null` when only
 * the waitlist bump was on offer). The version is what lets a NEW enabled
 * incentive re-surface the banner exactly once without ever re-showing it
 * for the same promise — see `shouldShowReferralBanner`.
 */
export type ReferralBannerAck = {
  status: "dismissed";
  incentiveVersion: string | null;
};

/**
 * Parses the raw backend value. Malformed input → null ("not dismissed") —
 * the failure mode is "show once more", never "silently hidden forever".
 */
export function decodeReferralBannerAck(raw: unknown): ReferralBannerAck | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  if (obj["status"] !== "dismissed") {
    return null;
  }
  const version = obj["incentive_version"];
  if (version !== null && version !== undefined && typeof version !== "string") {
    return null;
  }
  return { status: "dismissed", incentiveVersion: version ?? null };
}

/**
 * The banner's visibility rule over (dismissal, current incentive):
 *   - no dismissal → show;
 *   - dismissed, and a NEW enabled incentive (non-null version that differs
 *     from the one dismissed under) is live → show once more;
 *   - otherwise (same incentive, or the incentive was turned off since) → hide.
 * Turning an incentive OFF is deliberately not a resurface trigger: it is not
 * a "new enabled incentive", and re-showing weaker copy after a dismissal is
 * exactly the nag the persisted dismissal exists to prevent.
 */
export function shouldShowReferralBanner(
  ack: ReferralBannerAck | null,
  currentIncentiveVersion: string | null,
): boolean {
  if (ack === null) return true;
  return (
    currentIncentiveVersion !== null &&
    currentIncentiveVersion !== ack.incentiveVersion
  );
}

export function extractReferralBannerAckFromBootstrap(
  appState: Record<string, Record<string, unknown>> | null | undefined,
): ReferralBannerAck | null {
  const raw = appState?.[DASHBOARD_NAMESPACE]?.[REFERRAL_BANNER_KEY];
  return raw === undefined ? null : decodeReferralBannerAck(raw);
}

/**
 * PUT /api/users/me/app-state/consumer.dashboard/referral_banner.v1
 * Records the dismissal under the incentive version that was live.
 */
export async function putReferralBannerAck(
  incentiveVersion: string | null,
  token: string,
): Promise<AppStatePutResponse> {
  return api.put<AppStatePutResponse>(
    `/api/users/me/app-state/${DASHBOARD_NAMESPACE}/${REFERRAL_BANNER_KEY}`,
    { value: { status: "dismissed", incentive_version: incentiveVersion } },
    token,
  );
}

// ---------------------------------------------------------------------------
// referral_publish_prompt.v1 — one-time post-first-publish share prompt
// ---------------------------------------------------------------------------

/**
 * `shown` is written the first time the prompt renders (not on dismiss), so
 * a re-publish or a reload can never re-show it even if the user navigated
 * away without dismissing.
 */
export type ReferralPublishPromptAck = "shown";

export function decodeReferralPublishPromptAck(
  raw: unknown,
): ReferralPublishPromptAck | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  return (raw as Record<string, unknown>)["status"] === "shown" ? "shown" : null;
}

export function extractReferralPublishPromptAckFromBootstrap(
  appState: Record<string, Record<string, unknown>> | null | undefined,
): ReferralPublishPromptAck | null {
  const raw = appState?.[DASHBOARD_NAMESPACE]?.[REFERRAL_PUBLISH_PROMPT_KEY];
  return raw === undefined ? null : decodeReferralPublishPromptAck(raw);
}

/** PUT /api/users/me/app-state/consumer.dashboard/referral_publish_prompt.v1 */
export async function putReferralPublishPromptAck(
  token: string,
): Promise<AppStatePutResponse> {
  return api.put<AppStatePutResponse>(
    `/api/users/me/app-state/${DASHBOARD_NAMESPACE}/${REFERRAL_PUBLISH_PROMPT_KEY}`,
    { value: { status: "shown" } },
    token,
  );
}

// ---------------------------------------------------------------------------
// passkey_prompt.v1 — per-trigger passkey enrollment prompt dismissal
// ---------------------------------------------------------------------------

const AUTH_NAMESPACE = "consumer.auth";
const PASSKEY_PROMPT_KEY = "passkey_prompt.v1";

export type PasskeyPromptTrigger = "plaid_link" | "passport_publish";

/** ISO timestamp of the dismissal, per trigger. Present = never prompt again. */
export type PasskeyPromptState = Partial<Record<PasskeyPromptTrigger, string>>;

const PASSKEY_PROMPT_TRIGGERS: readonly PasskeyPromptTrigger[] = [
  "plaid_link",
  "passport_publish",
];

/**
 * Malformed input → `{}` ("not dismissed"): the failure mode is one extra
 * impression, never a permanent silent hide. Unknown keys are dropped.
 */
export function decodePasskeyPromptState(raw: unknown): PasskeyPromptState {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const obj = raw as Record<string, unknown>;
  const out: PasskeyPromptState = {};
  for (const trigger of PASSKEY_PROMPT_TRIGGERS) {
    const ts = obj[trigger];
    if (typeof ts === "string" && ts.length > 0) out[trigger] = ts;
  }
  return out;
}

export function extractPasskeyPromptStateFromBootstrap(
  appState: Record<string, Record<string, unknown>> | null | undefined,
): PasskeyPromptState | null {
  const raw = appState?.[AUTH_NAMESPACE]?.[PASSKEY_PROMPT_KEY];
  return raw === undefined ? null : decodePasskeyPromptState(raw);
}

/**
 * PUT /api/users/me/app-state/consumer.auth/passkey_prompt.v1
 * Writes the WHOLE object — callers merge their trigger into the state they
 * read so one trigger's dismissal never erases the other's.
 */
export async function putPasskeyPromptState(
  state: PasskeyPromptState,
  token: string,
): Promise<AppStatePutResponse> {
  return api.put<AppStatePutResponse>(
    `/api/users/me/app-state/${AUTH_NAMESPACE}/${PASSKEY_PROMPT_KEY}`,
    { value: state },
    token,
  );
}
